import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import CreateIcon from '@mui/icons-material/Create';
import { blue } from '@mui/material/colors';
import { usePermissions } from 'hooks/usePermissions';
import { GatewayRuleMapping, GatewayRuleMappingOrdersLink } from 'pages/GatewayRules/components';
import { useEnableAdditionalFields } from 'pages/GatewayRules/hooks/useEnableAdditionalFields';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Box } from 'shared/mui/Box';
import { LinkChip } from 'shared/mui/Chips';
import { IconButton } from 'shared/mui/IconButtons';
import { Typography } from 'shared/mui/Typography';
import { FunctionField, TextField } from 'shared/react-admin/Fields';
import { Mapping, MappingItem } from 'types/embedded/gatewayRule';

type Props = {
  mapping: Mapping;
  merchantId: string;
  onOpenGateway: (id?: string) => void;
  onOpenMerchant: (id?: string) => void;
  onOpenCommission: (id?: string) => void;
  onOpenPercentEdit: (mapping: Mapping) => void;
};

export const GatewayRulesShowMappingRow = ({
  onOpenGateway,
  onOpenMerchant,
  onOpenCommission,
  onOpenPercentEdit,
  merchantId,
  mapping,
}: Props) => {
  const { enableAdditionalFields } = useEnableAdditionalFields(merchantId);

  const translate = useTypedTranslate();
  const { hasResourceAccess } = usePermissions();

  return (
    <GatewayRuleMapping
      actionsSlot={<GatewayRuleMappingOrdersLink merchantId={merchantId} />}
      enableAdditionalFields={enableAdditionalFields}
    >
      {hasResourceAccess(Resources.Gateways) && (
        <FunctionField
          cellClassName="column-gateway"
          label={translate('app.table.gateway')}
          render={(record: MappingItem) =>
            record.gateway && (
              <Typography
                onClick={() => onOpenGateway(record.gateway?.['@id'])}
                sx={{
                  cursor: 'pointer',
                  color: blue[700],
                  textDecoration: 'underline',
                  fontSize: '14px',
                }}
              >
                {record.gateway.name}
              </Typography>
            )
          }
        />
      )}
      {hasResourceAccess('merchant_accounts') && (
        <FunctionField
          cellClassName="column-merchantAccount"
          label={translate('app.table.account')}
          render={(record: MappingItem) =>
            record.merchantAccount && (
              <Typography
                onClick={() => onOpenMerchant(record.merchantAccount?.['@id'])}
                sx={{
                  textDecoration: 'underline',
                  fontSize: '14px',
                  color: 'primary.main',
                  cursor: 'pointer',
                }}
              >
                {record.merchantAccount.name}
              </Typography>
            )
          }
        />
      )}
      {hasResourceAccess(Resources.Commissions) && (
        <FunctionField
          label={translate('app.table.commissions')}
          render={(record: MappingItem) => (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {record.commissions?.map((commission) => (
                <LinkChip
                  key={commission['@id']}
                  label={commission.name}
                  onClick={() => onOpenCommission(commission['@id'])}
                  size="medium"
                />
              ))}
            </Box>
          )}
        />
      )}
      {enableAdditionalFields && (
        <FunctionField
          label={translate('app.table.badge')}
          render={(record: MappingItem) => record.badge?.description ?? EMPTY_FIELD}
        />
      )}
      <TextField
        cellClassName="column-subagent"
        emptyText={EMPTY_FIELD}
        label={translate('app.merchantAccounts.subagentId')}
        source="subagent"
      />
      <TextField
        cellClassName="column-settings"
        label={
          <Box alignItems="center" display="flex" gap={1}>
            <Typography fontSize={14} fontWeight={500}>
              {translate('app.table.trafficPercentage')}
            </Typography>
            <IconButton onClick={() => onOpenPercentEdit(mapping)}>
              <CreateIcon />
            </IconButton>
          </Box>
        }
        sortable={false}
        source="value"
      />
    </GatewayRuleMapping>
  );
};
